import React from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { KNOWLEDGE_RESOURCES_TYPES, colorPalette } from 'shared/constants';
import { CategoryRowComponent } from 'src/components/common/categories/category-row';
import { faSpinner, faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import styles from './resource-manager.module.scss';
import { Popconfirm } from 'antd';
import { getLang } from 'shared/store/selectors/lang.selector';
import { useSelector } from 'react-redux';
import { useAccessiblePopconfirm } from 'src/components/design-system/popconfirm/hooks/use-accessible-popconfirm';
import { IconButton } from 'src/components/design-system';

const ResourceItemComponent = ({
  parsedResource = {},
  canEdit = false,
  disabled = false,
  disabledHint = '',
  loading = false,
  handleRemove = () => {},
  onOpenExternalUrl = () => {},
  onClick = () => {},
  wrapperClass = '',
  renderItemOptions = {},
  showTooltipRemoveConfirmation = true,
  onFocus,
}) => {
  const lang = useSelector(getLang('CONTENT_SELECTOR'));
  const isExternalUrl = parsedResource?.type === KNOWLEDGE_RESOURCES_TYPES.EXTERNAL_URL;

  const popconfirmProps = useAccessiblePopconfirm({
    onConfirm: async e => {
      e.stopPropagation();
      e.preventDefault();
      await handleRemove(e, parsedResource);
    },
    onCancel: e => e.stopPropagation(),
  });

  const handleStopPropagation = e => {
    e.stopPropagation();
    e.preventDefault();
  };

  const onRemoveClick = e => {
    e.stopPropagation();
    e.preventDefault();
    handleRemove(e, parsedResource);
  };

  const removeIcon = (
    <IconButton
      type="button"
      className={classNames(styles.removeIcon, { [styles.removeIconDisabled]: disabled })}
      label={disabled ? disabledHint : lang.DELETE_RESOURCE}
      disabled={disabled}
      onClick={showTooltipRemoveConfirmation ? undefined : onRemoveClick}
      tooltipProps={{ placement: 'right' }}>
      <FontAwesomeIcon
        icon={loading === parsedResource.id ? faSpinner : faTrashCan}
        spin={loading === parsedResource.id}
        color={colorPalette.red}
      />
    </IconButton>
  );

  const actions = !canEdit ? null : (
    <span role="presentation" onClick={handleStopPropagation}>
      {disabled || !showTooltipRemoveConfirmation ? (
        removeIcon
      ) : (
        <Popconfirm
          title={lang.DELETE_RESOURCE_CONFIRMATION}
          cancelText={lang.CANCEL_ACTION}
          okText={lang.DELETE_RESOURCE}
          okType="danger"
          okButtonProps={{ type: 'primary' }}
          cancelButtonProps={{ type: 'default' }}
          {...popconfirmProps}>
          {removeIcon}
        </Popconfirm>
      )}
    </span>
  );

  // Navigation props to forward to focusable elements
  const navProps = {
    onFocus,
  };

  if (isExternalUrl) {
    return (
      <a
        href={parsedResource.navigateTo}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.externalUrl}
        onClick={() => onOpenExternalUrl(parsedResource.navigateTo, parsedResource)}
        {...navProps}>
        <CategoryRowComponent
          {...parsedResource}
          actions={actions}
          wrapperClass={wrapperClass}
          renderItemOptions={renderItemOptions}
        />
      </a>
    );
  }

  return (
    <CategoryRowComponent
      {...parsedResource}
      actions={actions}
      onClick={() => onClick(parsedResource.navigateTo, parsedResource)}
      wrapperClass={wrapperClass}
      renderItemOptions={renderItemOptions}
    />
  );
};

export { ResourceItemComponent };
